// ============================================================================
// NpcLayer - Renders wildlife animals with animations
// ============================================================================

import {
  DietType,
  NPC_DEFINITIONS,
  NpcAIState,
  type NpcProperties,
  type NpcTypeValue,
  TILE_SIZE,
} from '@dreamgame/shared';
import { Container, Graphics, Rectangle, Sprite, Texture } from 'pixi.js';
import { isApproachingTarget } from '../../state/entities';
import {
  allNpcs,
  getInterpolatedNpcPosition,
  type NpcData,
  updateNpcAnimation,
} from '../../state/npcs';
import { isNpcSelected, selectedIds } from '../../state/selection';
import { COLORS } from '../constants';
import { loadTexture, onCategoryReload } from '../TextureManager';

// ============================================================================
// Types
// ============================================================================

interface NpcSpriteData {
  readonly container: Container;
  readonly sprite: Sprite;
  readonly shadowSprite: Sprite | null;
}

interface NpcTextureSet {
  readonly idle: Texture[];
  readonly walkRight: Texture[];
  readonly walkLeft: Texture[];
  readonly walkUp: Texture[];
  readonly walkDown: Texture[];
  readonly attack?: Texture[];
  readonly sleep?: Texture[];
}

// ============================================================================
// Module State
// ============================================================================

let container: Container;
let graphics: Graphics;

const npcSprites: Map<number, NpcSpriteData> = new Map();
const npcTextures: Map<NpcTypeValue, NpcTextureSet> = new Map();
let texturesLoaded = false;

// PERFORMANCE: Preallocated set to avoid GC pressure every frame
const _activeNpcIds = new Set<number>();

// Animation constants
const WALK_FRAME_DURATION = 120;
const IDLE_FRAME_DURATION = 400;
const DEFAULT_WALK_FRAMES = 4;
const DEFAULT_IDLE_FRAMES = 1;

// Set to false to load individual animal textures
const USE_RABBIT_FOR_ALL = false;
const RABBIT_TEXTURE_NAME = 'rabbit';

// ============================================================================
// Layer Creation
// ============================================================================

export const createNpcLayer = (): { container: Container; graphics: Graphics } => {
  container = new Container();
  graphics = new Graphics();
  return { container, graphics };
};

export const getNpcContainer = (): Container => container;

// ============================================================================
// Texture Loading
// ============================================================================

// Map animal type to texture name
const getNpcTextureName = ({ npcType }: { npcType: NpcTypeValue }): string => {
  // For testing, use rabbit for all animals
  if (USE_RABBIT_FOR_ALL) {
    return RABBIT_TEXTURE_NAME;
  }

  // Use the animal's definition name, sanitized for filenames
  // e.g., "Wild Boar" -> "wild_boar"
  const definition = NPC_DEFINITIONS.get(npcType);
  if (definition) {
    return definition.name.toLowerCase().replace(/\s+/g, '_');
  }
  return RABBIT_TEXTURE_NAME;
};

// Load all directional animations for a single texture name
const loadTextureSetForName = async ({
  textureName,
}: {
  textureName: string;
}): Promise<NpcTextureSet | null> => {
  const [idleTextures, walkRightTextures, walkLeftTextures, walkUpTextures, walkDownTextures] =
    await Promise.all([
      loadNpcAnimationFramesByName({
        textureName,
        animation: 'idle',
        frameCount: DEFAULT_IDLE_FRAMES,
      }),
      loadNpcAnimationFramesByName({
        textureName,
        animation: 'walk_right',
        frameCount: DEFAULT_WALK_FRAMES,
      }),
      loadNpcAnimationFramesByName({
        textureName,
        animation: 'walk_left',
        frameCount: DEFAULT_WALK_FRAMES,
      }),
      loadNpcAnimationFramesByName({
        textureName,
        animation: 'walk_up',
        frameCount: DEFAULT_WALK_FRAMES,
      }),
      loadNpcAnimationFramesByName({
        textureName,
        animation: 'walk_down',
        frameCount: DEFAULT_WALK_FRAMES,
      }),
    ]);

  // Use idle as fallback, or first frame of walk_right
  const fallbackFrames = idleTextures.length > 0 ? idleTextures : walkRightTextures.slice(0, 1);

  if (fallbackFrames.length === 0) {
    return null;
  }

  return {
    idle: idleTextures.length > 0 ? idleTextures : fallbackFrames,
    walkRight: walkRightTextures.length > 0 ? walkRightTextures : fallbackFrames,
    walkLeft: walkLeftTextures.length > 0 ? walkLeftTextures : fallbackFrames,
    walkUp: walkUpTextures.length > 0 ? walkUpTextures : fallbackFrames,
    walkDown: walkDownTextures.length > 0 ? walkDownTextures : fallbackFrames,
  };
};

const loadRabbitTexturesForAll = async ({
  npcTypes,
}: {
  npcTypes: ReadonlyArray<NpcProperties>;
}): Promise<void> => {
  try {
    const textureSet = await loadTextureSetForName({ textureName: RABBIT_TEXTURE_NAME });
    if (!textureSet) {
      console.warn('Failed to load rabbit texture set');
      return;
    }

    // Assign rabbit textures to all animal types
    for (const definition of npcTypes) {
      npcTextures.set(definition.type, textureSet);
    }

    console.log('Loaded directional textures:', {
      idle: textureSet.idle.length,
      walkRight: textureSet.walkRight.length,
      walkLeft: textureSet.walkLeft.length,
      walkUp: textureSet.walkUp.length,
      walkDown: textureSet.walkDown.length,
    });
  } catch (error) {
    console.warn('Failed to load rabbit textures:', error);
  }
};

const loadIndividualNpcTextures = async ({
  npcTypes,
}: {
  npcTypes: ReadonlyArray<NpcProperties>;
}): Promise<void> => {
  for (const definition of npcTypes) {
    try {
      const textureName = getNpcTextureName({ npcType: definition.type });
      const textureSet = await loadTextureSetForName({ textureName });

      if (textureSet) {
        npcTextures.set(definition.type, textureSet);
      }
    } catch {
      // Texture not found, will use fallback graphics
    }
  }
};

export const loadNpcTextures = async (): Promise<void> => {
  const npcTypes = [...NPC_DEFINITIONS.values()];

  if (USE_RABBIT_FOR_ALL) {
    await loadRabbitTexturesForAll({ npcTypes });
  } else {
    await loadIndividualNpcTextures({ npcTypes });
  }

  texturesLoaded = npcTextures.size > 0;
  console.log(`Loaded textures for ${npcTextures.size} animal types`);
};

const loadNpcAnimationFramesByName = async ({
  textureName,
  animation,
  frameCount,
}: {
  textureName: string;
  animation: string;
  frameCount: number;
}): Promise<Texture[]> => {
  const frames: Texture[] = [];

  // Try to load a sprite strip first (e.g., rabbit_walk.png)
  try {
    const stripPath = `/animals/${textureName}_${animation}.png`;
    const stripTexture = await loadTexture({ path: stripPath });

    // If it's a strip and we expect multiple frames, extract them
    if (stripTexture && frameCount > 1) {
      const frameWidth = stripTexture.width / frameCount;
      for (let i = 0; i < frameCount; i++) {
        const frame = new Texture({
          source: stripTexture.source,
          frame: new Rectangle(i * frameWidth, 0, frameWidth, stripTexture.height),
        });
        frames.push(frame);
      }
      return frames;
    }

    // Single frame texture
    frames.push(stripTexture);
    return frames;
  } catch {
    // Try individual frame files (e.g., rabbit_walk_0.png, rabbit_walk_1.png)
    for (let i = 0; i < frameCount; i++) {
      try {
        const framePath = `/animals/${textureName}_${animation}_${i}.png`;
        const frameTexture = await loadTexture({ path: framePath });
        frames.push(frameTexture);
      } catch {
        // Frame not found
      }
    }
  }

  return frames;
};

export const areNpcTexturesLoaded = (): boolean => texturesLoaded;

// Reload textures (clears sprites and reloads from TextureManager)
export const reloadNpcTextures = async (): Promise<void> => {
  // Clear all existing sprites
  clearNpcSprites();

  // Clear texture cache
  npcTextures.clear();
  texturesLoaded = false;

  // Reload textures
  await loadNpcTextures();
  console.log('Animal textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'animals', callback: reloadNpcTextures });

// ============================================================================
// Rendering
// ============================================================================

export const drawNpcs = (): void => {
  graphics.clear();

  const animalList = allNpcs.value;
  const useSprites = texturesLoaded;

  // PERFORMANCE: Reuse preallocated set
  _activeNpcIds.clear();

  for (const npc of animalList) {
    _activeNpcIds.add(npc.networkId);

    // Skip dead animals (they become corpses)
    if (npc.aiState === NpcAIState.DEAD) {
      continue;
    }

    const pos = getInterpolatedNpcPosition({ npc });
    const worldX = pos.x * TILE_SIZE;
    const worldY = pos.y * TILE_SIZE;
    const definition = NPC_DEFINITIONS.get(npc.npcType);

    if (!definition) {
      continue;
    }

    // Calculate render size based on animal definition
    const renderWidth = definition.spriteSize * TILE_SIZE;
    const renderHeight = definition.spriteSize * TILE_SIZE;
    const offsetX = (TILE_SIZE - renderWidth) / 2;
    const offsetY = (TILE_SIZE - renderHeight) / 2;

    // Try to use sprites
    if (useSprites && npcTextures.has(npc.npcType)) {
      const spriteData = updateNpcSprite({
        npc,
        parentContainer: container,
        renderWidth,
        renderHeight,
      });

      if (spriteData) {
        spriteData.container.x = worldX + offsetX;
        spriteData.container.y = worldY + offsetY;

        // Tint based on state
        const tint = getNpcTint({ npc });
        spriteData.sprite.tint = tint;
      }
    } else {
      // Fallback graphics rendering
      drawNpcFallback({
        worldX,
        worldY,
        npc,
        definition,
        offsetX,
        offsetY,
        renderWidth,
        renderHeight,
      });
    }

    // Draw selection outline
    const selected = isNpcSelected({ networkId: npc.networkId });
    if (selected) {
      graphics.setStrokeStyle({ width: 2, color: COLORS.SELECTION_STROKE });
      graphics.roundRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4, 6);
      graphics.stroke();
    }

    // Draw attack target indicator (red square) - only when selected pawn is approaching
    const isTarget = isApproachingTarget({
      networkId: npc.networkId,
      selectedPawnIds: selectedIds.value,
    });
    if (isTarget) {
      // Red fill with low opacity
      graphics.setFillStyle({ color: 0xff0000, alpha: 0.15 });
      graphics.roundRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4, 6);
      graphics.fill();

      // Red border
      graphics.setStrokeStyle({ width: 2, color: 0xff4444 });
      graphics.roundRect(worldX + 2, worldY + 2, TILE_SIZE - 4, TILE_SIZE - 4, 6);
      graphics.stroke();
    }

    // Draw health bar
    drawNpcHealthBar({ worldX, worldY, npc });
  }

  // Cleanup removed animals
  cleanupRemovedNpcs({ activeNetworkIds: _activeNpcIds });
};

const drawNpcFallback = ({
  worldX,
  worldY,
  npc,
  definition,
  offsetX,
  offsetY,
  renderWidth,
  renderHeight,
}: {
  worldX: number;
  worldY: number;
  npc: NpcData;
  definition: NpcProperties;
  offsetX: number;
  offsetY: number;
  renderWidth: number;
  renderHeight: number;
}): void => {
  // Color based on animal category
  const baseColor = getNpcBaseColor({ definition });

  // Draw shadow
  graphics.setFillStyle({ color: 0x000000, alpha: 0.2 });
  graphics.ellipse(worldX + TILE_SIZE / 2, worldY + TILE_SIZE - 2, renderWidth / 2, 4);
  graphics.fill();

  // Draw body
  graphics.setFillStyle({ color: baseColor });
  graphics.roundRect(worldX + offsetX, worldY + offsetY, renderWidth, renderHeight, 4);
  graphics.fill();

  // Draw eyes direction based on velocity
  const eyeSize = Math.max(2, renderWidth / 8);
  const eyeOffsetX = npc.vx > 0 ? 2 : npc.vx < 0 ? -2 : 0;
  const eyeY = worldY + offsetY + renderHeight / 4;

  graphics.setFillStyle({ color: 0xffffff });
  graphics.circle(worldX + TILE_SIZE / 2 - 3 + eyeOffsetX, eyeY, eyeSize);
  graphics.circle(worldX + TILE_SIZE / 2 + 3 + eyeOffsetX, eyeY, eyeSize);
  graphics.fill();

  // Pupils
  graphics.setFillStyle({ color: 0x000000 });
  graphics.circle(worldX + TILE_SIZE / 2 - 3 + eyeOffsetX, eyeY, eyeSize / 2);
  graphics.circle(worldX + TILE_SIZE / 2 + 3 + eyeOffsetX, eyeY, eyeSize / 2);
  graphics.fill();
};

const getNpcBaseColor = ({ definition }: { definition: NpcProperties }): number => {
  // Different colors for different animal diets
  switch (definition.diet) {
    case DietType.CARNIVORE:
      return 0x8b4513; // Brown
    case DietType.HERBIVORE:
      return 0x228b22; // Forest green
    case DietType.OMNIVORE:
      return 0x696969; // Gray
    case DietType.SCAVENGER:
      return 0x4a4a4a; // Dark gray
    case DietType.PISCIVORE:
      return 0x4169e1; // Blue
    case DietType.INSECTIVORE:
      return 0x32cd32; // Lime green
    case DietType.FILTER_FEEDER:
      return 0x87ceeb; // Sky blue
    default:
      return 0x808080; // Gray
  }
};

const getNpcTint = ({ npc }: { npc: NpcData }): number => {
  // Tint based on state
  if (npc.aiState === NpcAIState.SLEEPING) {
    return 0x8888ff; // Blue tint for sleeping
  }
  if (npc.aiState === NpcAIState.FLEEING) {
    return 0xffff88; // Yellow tint for fleeing
  }
  if (npc.aiState === NpcAIState.HUNTING || npc.aiState === NpcAIState.FIGHTING) {
    return 0xff8888; // Red tint for aggressive
  }
  return 0xffffff; // Normal
};

// ============================================================================
// Health and State Indicators
// ============================================================================

const drawNpcHealthBar = ({
  worldX,
  worldY,
  npc,
}: {
  worldX: number;
  worldY: number;
  npc: NpcData;
}): void => {
  // Only show health bar if damaged
  if (npc.health >= npc.maxHealth) {
    return;
  }

  const healthBarY = worldY - 6;
  const barWidth = TILE_SIZE - 8;

  // Background
  graphics.setFillStyle({ color: COLORS.HEALTH_BACKGROUND });
  graphics.rect(worldX + 4, healthBarY, barWidth, 3);
  graphics.fill();

  // Health bar
  const healthPercent = npc.health / npc.maxHealth;
  const healthColor =
    healthPercent > 0.6
      ? COLORS.HEALTH_HIGH
      : healthPercent > 0.3
        ? COLORS.HEALTH_MEDIUM
        : COLORS.HEALTH_LOW;

  graphics.setFillStyle({ color: healthColor });
  graphics.rect(worldX + 4, healthBarY, barWidth * healthPercent, 3);
  graphics.fill();
};

const _drawAIStateIndicator = ({
  worldX,
  worldY,
  npc,
}: {
  worldX: number;
  worldY: number;
  npc: NpcData;
}): void => {
  // Small indicator for AI state
  const indicatorSize = 4;
  const indicatorX = worldX + TILE_SIZE - indicatorSize - 2;
  const indicatorY = worldY + 2;

  let color: number;
  switch (npc.aiState) {
    case NpcAIState.SLEEPING:
      color = 0x0000ff; // Blue
      break;
    case NpcAIState.EATING:
      color = 0x00ff00; // Green
      break;
    case NpcAIState.HUNTING:
    case NpcAIState.FIGHTING:
      color = 0xff0000; // Red
      break;
    case NpcAIState.FLEEING:
      color = 0xffff00; // Yellow
      break;
    case NpcAIState.WANDERING:
      color = 0x888888; // Gray
      break;
    default:
      color = 0xffffff; // White (idle)
  }

  graphics.setFillStyle({ color, alpha: 0.8 });
  graphics.circle(indicatorX, indicatorY, indicatorSize / 2);
  graphics.fill();
};

// ============================================================================
// Sprite Management
// ============================================================================

// Determine which directional animation to use based on velocity
const getDirectionalFrames = ({
  textureSet,
  vx,
  vy,
}: {
  textureSet: NpcTextureSet;
  vx: number;
  vy: number;
}): Texture[] => {
  const absVx = Math.abs(vx);
  const absVy = Math.abs(vy);

  // If not moving, use idle
  if (absVx < 0.01 && absVy < 0.01) {
    return textureSet.idle;
  }

  // Determine primary direction (stronger velocity axis)
  if (absVx > absVy) {
    // Horizontal movement dominates
    return vx > 0 ? textureSet.walkRight : textureSet.walkLeft;
  } else {
    // Vertical movement dominates
    return vy > 0 ? textureSet.walkDown : textureSet.walkUp;
  }
};

const updateNpcSprite = ({
  npc,
  parentContainer,
  renderWidth,
  renderHeight,
}: {
  npc: NpcData;
  parentContainer: Container;
  renderWidth: number;
  renderHeight: number;
}): NpcSpriteData | null => {
  const textureSet = npcTextures.get(npc.npcType);
  if (!textureSet) {
    return null;
  }

  const existing = npcSprites.get(npc.networkId);
  const isMoving = Math.abs(npc.vx) > 0.01 || Math.abs(npc.vy) > 0.01;
  const frames = getDirectionalFrames({ textureSet, vx: npc.vx, vy: npc.vy });
  const frameDuration = isMoving ? WALK_FRAME_DURATION : IDLE_FRAME_DURATION;

  if (existing) {
    // Update animation
    const timeSinceLastFrame = performance.now() - npc.lastFrameTime;
    if (timeSinceLastFrame >= frameDuration) {
      const nextFrame = (npc.animationFrame + 1) % frames.length;
      updateNpcAnimation({
        networkId: npc.networkId,
        frame: nextFrame,
        time: performance.now(),
      });

      const texture = frames[nextFrame];
      if (texture) {
        existing.sprite.texture = texture;
        // Re-apply size after texture change
        existing.sprite.width = renderWidth;
        existing.sprite.height = renderHeight;
      }
    } else {
      // Ensure correct texture even if frame didn't advance (direction may have changed)
      const currentTexture = frames[npc.animationFrame % frames.length];
      if (currentTexture && existing.sprite.texture !== currentTexture) {
        existing.sprite.texture = currentTexture;
        existing.sprite.width = renderWidth;
        existing.sprite.height = renderHeight;
      }
    }

    return existing;
  }

  // Create new sprite
  const spriteContainer = new Container();
  const texture = frames[0];
  if (!texture) {
    return null;
  }

  const sprite = new Sprite(texture);
  sprite.width = renderWidth;
  sprite.height = renderHeight;
  sprite.anchor.set(0.5, 0.5);
  sprite.x = renderWidth / 2;
  sprite.y = renderHeight / 2;

  spriteContainer.addChild(sprite);
  parentContainer.addChild(spriteContainer);

  const spriteData: NpcSpriteData = {
    container: spriteContainer,
    sprite,
    shadowSprite: null,
  };

  npcSprites.set(npc.networkId, spriteData);
  return spriteData;
};

const cleanupRemovedNpcs = ({ activeNetworkIds }: { activeNetworkIds: Set<number> }): void => {
  for (const networkId of npcSprites.keys()) {
    if (!activeNetworkIds.has(networkId)) {
      const spriteData = npcSprites.get(networkId);
      if (spriteData) {
        spriteData.container.destroy({ children: true });
        npcSprites.delete(networkId);
      }
    }
  }
};

// ============================================================================
// Cleanup
// ============================================================================

export const removeNpcSprite = ({ networkId }: { networkId: number }): void => {
  const spriteData = npcSprites.get(networkId);
  if (spriteData) {
    spriteData.container.destroy({ children: true });
    npcSprites.delete(networkId);
  }
};

export const clearNpcSprites = (): void => {
  for (const spriteData of npcSprites.values()) {
    spriteData.container.destroy({ children: true });
  }
  npcSprites.clear();
};
